import { useMemo } from "react";
import {
    changedCounterpartyDetails,
    CounterpartyDetailChanges,
    EMPTY_COUNTERPARTY_DETAIL_CHANGES,
    ExtractedCounterpartyParty,
    savedCounterpartyDetails,
} from "@common/helpers/counterpartyDetailChanges";
import { extractedCounterpartyParty } from "@common/helpers/extractedCounterpartyParty";
import { hasActiveImporterError } from "@common/helpers/financialDocument";
import { CounterpartyDetails } from "@common/schemas/counterpartyDetails.schema";
import {
    FinancialDocumentExtended,
    ImporterErrors,
} from "@common/types/financialDocument";
import { useCounterpartyDetailsQuery } from "@api/counterparty.api";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { getAccrualExtraction } from "./accrualImporter.helpers";
import { getExtractionOrderIndex } from "./useAccrualImporterCounterpartyEditor.helpers";

export function useAccrualCounterpartyDetailDiff({
    financialDocument,
    extractionId,
    counterpartyId,
    mode,
}: {
    financialDocument: FinancialDocumentExtended;
    extractionId: string | null;
    counterpartyId: string | null;
    mode: AccrualDocumentImporterMode;
}): {
    changes: CounterpartyDetailChanges;
    extracted: ExtractedCounterpartyParty | null;
    details: CounterpartyDetails | undefined;
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
} {
    const hasServerError = hasActiveImporterError({
        document: financialDocument,
        error: ImporterErrors.ACCRUAL_COUNTERPARTY_DETAILS_CHANGED,
    });
    const flaggedCounterpartyId = hasServerError ? counterpartyId : null;
    const detailsQuery = useCounterpartyDetailsQuery({
        counterpartyId: flaggedCounterpartyId ?? undefined,
        enabled: Boolean(flaggedCounterpartyId),
    });
    const details = flaggedCounterpartyId ? detailsQuery.data : undefined;

    const extracted = useMemo(() => {
        const orderIndex =
            extractionId == null
                ? null
                : getExtractionOrderIndex({
                      financialDocument,
                      extractionId,
                      mode,
                  });
        if (orderIndex == null) {
            return null;
        }
        return extractedCounterpartyParty({
            document:
                getAccrualExtraction(financialDocument)?.originalExtraction
                    ?.documents[orderIndex],
            party: mode === "bills" ? "issuer" : "recipient",
        });
    }, [extractionId, financialDocument, mode]);

    const changes = useMemo(() => {
        if (!extracted || !details) {
            return EMPTY_COUNTERPARTY_DETAIL_CHANGES;
        }
        return changedCounterpartyDetails({
            extracted,
            saved: savedCounterpartyDetails(details),
        });
    }, [details, extracted]);

    return {
        changes,
        extracted,
        details,
        isLoading: Boolean(flaggedCounterpartyId) && detailsQuery.isLoading,
        isError: Boolean(flaggedCounterpartyId) && detailsQuery.isError,
        refetch: () => {
            void detailsQuery.refetch();
        },
    };
}
